// src/components/partner/RecentTransactionsTable.tsx
//
// RecentTransactionsTable
// - Renders partner transaction history using the shared table UI (StandardTable*).
// - Provides consistent pagination UX (top: total + jump, bottom: pagination).
// - Avoids row background highlighting to prevent visual noise; only refunded rows are marked as inactive.
//
// Notes
// - This component is UI-only; it does not own data fetching.
// - The parent page controls pagination state and passes `transactions`, `page`, and `totalCount`.

import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';

import { timestampToDate } from '@utils/time';
import { toUsdDisplay } from '@utils/money';
import { getPartnerTransactionTypeMeta } from '@constants/partnerLabels';

import {
  StandardTableFrame,
  StandardTableToolbar,
  StandardPagination,
  TableRowStatus,
  cellSx,
} from '@components/common/table';

export type PartnerTransactionRow = {
  partnerTransactionId: number | string;
  type: string;

  partnerTransactionCode: string;
  orderCode: string;
  productCode: string;
  productName: string;

  quantity: number;
  previousBalance: number;
  addedAmount: number;

  balance?: number;
  createdAt: string | number;
};

type Props = {
  transactions: PartnerTransactionRow[];
  isLoading: boolean;

  page: number;
  totalCount: number;
  pageSize: number;

  onChangePage: (_: React.ChangeEvent<unknown>, value: number) => void;
  skeletonRows?: number;
};

const RecentTransactionsTable = React.forwardRef<HTMLDivElement, Props>(
  (
    { transactions, isLoading, page, totalCount, pageSize, onChangePage, skeletonRows = 6 },
    ref,
  ) => {
    const rows = useMemo(() => transactions ?? [], [transactions]);

    const handleChangePage = useCallback(
      (next: number) => onChangePage({} as any, next),
      [onChangePage],
    );

    /**
     * Maps a transaction type to a compact Chip representation.
     * Unknown types fall back to the raw string for forward compatibility.
     */
    const renderTypeChip = useCallback((rawType: string) => {
      const meta = getPartnerTransactionTypeMeta(rawType);

      return (
        <Chip
          size="small"
          label={meta.label}
          color={meta.chip.color}
          variant={meta.chip.variant}
          sx={{ fontWeight: 700, fontSize: 12 }}
        />
      );
    }, []);

    /**
     * Row status policy
     * - Do not use background-based highlighting to avoid visual clutter.
     * - Mark refunded transactions as "inactive" to provide subtle emphasis.
     */
    const getRowStatus = useCallback((t: PartnerTransactionRow): TableRowStatus => {
      if (String(t.type) === 'REFUNDED_ORDER') return 'inactive';
      return 'normal';
    }, []);

    const getFinalBalance = useCallback((t: PartnerTransactionRow) => {
      const amount = Number(t.addedAmount ?? 0);
      return typeof t.balance === 'number'
        ? t.balance
        : Number(t.previousBalance ?? 0) + amount;
    }, []);

    return (
      <Box ref={ref}>
        {/* Section title */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <ReceiptLong fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Recent Transactions
          </Typography>
        </Stack>

        {/* Top: Total + Page Jump */}
        <StandardTableToolbar
          page={page}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          showTotalCount
          showPageJump
        />

        {/* Table */}
        <Box mt={1}>
          <StandardTableFrame<PartnerTransactionRow>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell align="center">Transaction</TableCell>
                <TableCell>Transaction Code</TableCell>
                <TableCell>Order Code</TableCell>
                <TableCell>Product Code</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Prev Balance</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Final Balance</TableCell>
                <TableCell align="center">Date (KST)</TableCell>
              </TableRow>
            }
            rows={rows}
            getRowKey={(t) => t.partnerTransactionId}
            columnsCount={11}
            isLoading={isLoading}
            loadingVariant="skeleton"
            skeletonRows={skeletonRows}
            emptyText="No transactions found."
            minWidth={1180}
            getRowStatus={getRowStatus}
            renderRow={(t, index) => {
              const amount = Number(t.addedAmount ?? 0);
              const finalBalance = getFinalBalance(t);

              return (
                <>
                  <TableCell align="right" sx={cellSx.number}>
                    {(page - 1) * pageSize + index + 1}
                  </TableCell>

                  <TableCell align="center" sx={cellSx.nowrap}>
                    {renderTypeChip(t.type)}
                  </TableCell>

                  <TableCell sx={cellSx.mono}>{t.partnerTransactionCode}</TableCell>
                  <TableCell sx={cellSx.mono}>{t.orderCode}</TableCell>
                  <TableCell sx={cellSx.mono}>{t.productCode}</TableCell>

                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t.productName}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={cellSx.number}>
                    {t.quantity}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdDisplay(t.previousBalance)}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdDisplay(amount)}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdDisplay(finalBalance)}
                  </TableCell>

                  <TableCell align="center" sx={cellSx.nowrap}>
                    {timestampToDate(t.createdAt)}
                  </TableCell>
                </>
              );
            }}
          />
        </Box>

        {/* Bottom: Pagination */}
        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount ?? 0}
            onChangePage={handleChangePage}
            size="medium"
            shape="rounded"
          />
        </Box>
      </Box>
    );
  },
);

RecentTransactionsTable.displayName = 'RecentTransactionsTable';

export default RecentTransactionsTable;
